// ============================================================================
// Revenue Widget — current MRR + ARR headline numbers and a 13-month bar
// chart of "MRR added by month" (drawn from facilities.contract_start).
//
// Why MRR-added rather than total MRR by month: the database doesn't have
// time-series billing snapshots, so we can't reconstruct historical total
// MRR. What we *can* show authoritatively is when each currently-active
// facility went live, which makes the chart a real growth signal.
// ============================================================================

import type { RevenueSnapshot } from './use-dashboard-data';
import {
  WidgetCard,
  WidgetSkeleton,
  fmtCompactCurrency,
} from './widget-card';

interface Props {
  data: RevenueSnapshot | null;
  loading: boolean;
}

export function RevenueWidget({ data, loading }: Props) {
  if (loading && !data) {
    return (
      <WidgetCard title="Revenue" minHeight={240}>
        <WidgetSkeleton lines={4} />
      </WidgetCard>
    );
  }

  if (!data) {
    return (
      <WidgetCard title="Revenue" minHeight={240}>
        <p className="text-sm text-tcs-text-muted">No revenue data available.</p>
      </WidgetCard>
    );
  }

  const last3 = data.mrr_added_by_month.slice(-3);
  const last3Total = last3.reduce((s, b) => s + b.mrr_added, 0);
  const last3Count = last3.reduce((s, b) => s + b.count, 0);

  return (
    <WidgetCard
      title="Revenue"
      subtitle={`${data.paying_facilities} active facilities billing`}
      minHeight={240}
    >
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div>
          <div className="text-2xl font-semibold tabular-nums text-tcs-text">
            {fmtCompactCurrency(data.current_mrr)}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
            Current MRR
          </div>
        </div>
        <div>
          <div className="text-lg font-medium tabular-nums text-tcs-text-secondary">
            {fmtCompactCurrency(data.current_arr)}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
            Run-rate ARR
          </div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-sm font-medium text-tcs-text-secondary">
            +{fmtCompactCurrency(last3Total)} / {last3Count}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
            Last 3 mo · MRR / facilities
          </div>
        </div>
      </div>

      <MrrBars months={data.mrr_added_by_month} />

      {data.missing_rate_count > 0 && (
        <p className="mt-2 text-xs text-tcs-text-muted">
          {data.missing_rate_count} active{' '}
          {data.missing_rate_count === 1 ? 'facility is' : 'facilities are'}{' '}
          missing a monthly_rate — MRR is a floor, not a ceiling.
        </p>
      )}
    </WidgetCard>
  );
}

// ----- Bar chart ------------------------------------------------------------
// 13 monthly bars with the most-recent month on the right. Bar height is
// proportional to the largest bar so the chart auto-scales as TCS grows.

function MrrBars({
  months,
}: {
  months: { month: string; mrr_added: number; count: number }[];
}) {
  const HEIGHT = 90;
  const maxAdded = Math.max(1, ...months.map((m) => m.mrr_added));
  return (
    <div className="mt-4">
      <div
        className="flex items-end gap-1"
        style={{ height: HEIGHT }}
        role="img"
        aria-label="MRR added per month over the last 13 months"
      >
        {months.map((m, i) => {
          const h = Math.max(2, (m.mrr_added / maxAdded) * HEIGHT);
          // Highlight the trailing (current) month so it visually anchors the
          // "where are we now" reading. Earlier months are uniform slate.
          const isCurrent = i === months.length - 1;
          return (
            <div
              key={m.month}
              className="flex h-full flex-1 items-end"
              title={`${m.month}: ${fmtCompactCurrency(m.mrr_added)} added across ${m.count} ${m.count === 1 ? 'facility' : 'facilities'}`}
            >
              <div
                className={
                  isCurrent
                    ? 'w-full rounded-sm bg-tcs-inverse'
                    : 'w-full rounded-sm bg-[#5B8DB8]'
                }
                style={{ height: h }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] tabular-nums text-tcs-text-muted">
        <span>{months[0]?.month}</span>
        <span>{months[Math.floor(months.length / 2)]?.month}</span>
        <span>{months[months.length - 1]?.month}</span>
      </div>
    </div>
  );
}
